/**
 * Mail (`/{ws}/mail`, logic/mail): every mailbox I'm a member of in one list, or one of them when the Mailbox filter picks
 * it (the route's, so it stays across folders and tabs until cleared). The folder in view — the inbox split into the tabs the server
 * defines (MailTabDef: Priority, All, Clients, Team, Promotions, Updates today, each with "N new" and the newest
 * sender) — search, and the thread actions (read/unread, star, archive, trash, spam, move to another tab, Mark as
 * priority / Not priority). The strip comes with every inbox listing and is cached on the device (KeyValueStorage), so
 * it draws at once and changes without an app release. Mail opens on the strip's first tab, Priority, unless it's empty
 * (logic/mail mailLandingTab). One per workspace, shared by the Mail screen and Mail's badge (unread Priority threads).
 * Realtime `mail.thread.updated` / `mail.mailbox.*` events keep it live; actions are optimistic and a refused one
 * refetches. Online only.
 *
 * Switching tabs is Gmail's: each list (mailbox × folder × label × tab) is kept in memory, so opening one shows it at
 * once and refreshes it underneath; one never seen shows a skeleton (status "loading"), never the previous tab's
 * conversations. A response for a list I've left is never shown (only kept for later). After the inbox first loads, the strip's other tabs are
 * fetched in the background, so the first switch is instant too. The strip's counts and previews come with every
 * listing and only a newer request's replace them, so they never step back. Searches, further pages and those
 * background fetches leave the counts out (`counts: "none"`): each list costs the server far less without them.
 *
 * The filter row (logic/mailQuery `MailFilters`) is the list's `filter`, the same text a search would be. Unread, Starred
 * and the rest belong to the list in view and go when it changes; a filtered list isn't kept. In the unified list the
 * server collapses mail that reached two of my mailboxes (`MailThread.copies`): a thread event can't say whether a thread
 * it hasn't listed is such a copy, so one that would show reloads the list instead of adding a row.
 *
 * Searching shows the matches among the kept lists on the first keystroke (logic/mailQuery `mailQueryLocal`), then the
 * server's answer in their place. The same list asked for again while it's in flight is one request (`listOnce`), unless something changed (`fresh`);
 * a newer `load` cancels a search still running.
 *
 * On a phone Mail opens from a device snapshot (ports SnapshotStore, one file per workspace and user: the inbox I last saw
 * across all my mailboxes, its tab, counts and first page): shown as ready on that tab, Mail's `landing` pick off so
 * no other list flashes, then replaced quietly. `revalidate` is that refresh, on open, back in the foreground and on pull,
 * and `freshness` says how it goes: "updating" once it's run for a moment, "updated" with nothing new, "stale" when it
 * failed and the list stays. Rows newer than the list had are `newIds`, for the screen's accent and "N new" pill, and mail
 * that arrives live joins them without a line or a caption. The web has no store and calls `refresh`, so none of this
 * shows there.
 */
import type {
  ListMailThreadsQuery, ListMailThreadsResponse, MailDraft, MailFolder, MailLabel, MailListFolder, MailPriorityReason, MailPrioritySummary, MailTabDef, MailTabId, MailTabSummary, MailThread, Mailbox,
  PersistentEvent, SaveMailLabelRequest, UpdateMailThreadRequest,
} from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import type { MailView } from "../logic/shell.ts";
import { displayName, homeMailboxId, mailLabelIds, mailLandingTab, mailWaitTabs, threadPriority } from "../logic/mail.ts";
import { hasListFilters, listFiltersOf, mailFilterQuery, mailQueryLocal, parseMailQuery, type MailFilters } from "../logic/mailQuery.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type MailThreadRow = {
  id: string;
  thread: MailThread;
  /** "Ana, Bia (3)": the people on it (first names / local parts), message count when > 1. */
  senders: string;
  subject: string;
  snippet: string;
  unread: boolean;
  starred: boolean;
  hasAttachments: boolean;
  phishing: boolean;
  needsReply: boolean;
  /** Why it's in Priority (the marker), absent = it isn't. */
  priority?: MailPriorityReason;
  lastAt: number;
};

export type MailboxViewState = {
  status: "loading" | "ready" | "failed" | "empty";
  mailboxes: Mailbox[];
  /** The mailbox new mail starts from: the one in view, else my own (logic/mail homeMailboxId). */
  mailboxId?: string;
  /** The list reads every mailbox I'm a member of (I have more than one and the Mailbox filter hasn't picked one). */
  unified: boolean;
  /** The filter row. `mailbox` is the address of the mailbox in view. */
  filters: MailFilters;
  /** A folder, Gmail's views (starred, snoozed, scheduled) or my drafts. */
  folder: MailView;
  /** A label's conversations across folders (instead of `folder`). */
  label?: string;
  /** The open inbox tab ("" until the strip is known: every tab). */
  tab: MailTabId;
  /** The strip: the server's tabs in order (MailTabDef), from the device's cache until the server answers. */
  tabDefs: MailTabDef[];
  /** The labels of the mailboxes in view (rail: logic/mail mergedMailLabels; "Label as": the row's mailbox's). */
  labels: MailLabel[];
  /** My drafts in the mailboxes in view, when `folder` is "drafts". */
  drafts: MailDraft[];
  /** Conversations ticked for a bulk action (Gmail's checkboxes), in list order. */
  selected: string[];
  /** Search text; non-empty = search results across folders. */
  query: string;
  threads: MailThread[];
  rows: MailThreadRow[];
  /** Each category tab's counts. */
  tabs: MailTabSummary[];
  /** The Priority tab, first in the strip (absent until the mailbox's inbox has loaded). */
  priority?: MailPrioritySummary;
  nextCursor?: string;
  loadingMore: boolean;
  /** Unread Priority threads across my mailboxes (Mail's badge; each mailbox's Inbox row keeps its own unread). */
  unread: number;
  offline: boolean;
  /** How the refresh over the list on screen is going (the strip under the filter row). */
  freshness: MailFreshness;
  /** When the server last answered a list (the snapshot's, after a restart); "Updated 2h ago". */
  lastUpdatedAt?: number;
  /** Rows that came in with the last refresh, until they've been seen: the accent, the "N new" pill. */
  newIds: string[];
};

/** "updating": a refresh has run for a moment. "updated": it found nothing new. "stale": it failed; the list stays. */
export type MailFreshness = "idle" | "updating" | "updated" | "stale";

const PAGE = 50;
/** Lists kept in memory (each the first page or more of one view), oldest dropped first. */
const CACHED_LISTS = 40;
/** What a list's `mailboxId` is when it reads all my mailboxes. */
const ALL = "*";
/** Thread events close together reload the unified list once. */
const RELOAD_MS = 400;
/** A refresh this quick shows no line. */
const UPDATING_MS = 300;
/** How long "Updated just now" stays. */
const UPDATED_MS = 2000;
/** How long a new row's accent stays once the top of the list is in view. */
const NEW_ROW_MS = 1600;
/** Coming back this soon after an answer is the same look (the notification shade, Face ID): no refresh. */
const FRESHEN_GAP_MS = 10_000;
/** Changes close together write the snapshot once. */
const SNAPSHOT_SAVE_MS = 500;
const SNAPSHOT_VERSION = 1;

/** What a list shows: a folder (or a label's conversations), the inbox split by tab. */
type ListView = { mailboxId: string; folder: MailView; label: string | undefined; tab: MailTabId };
/** `seq`: the request (or change) it came from; an answer only replaces a kept list from an earlier one. */
type CachedList = { view: ListView; threads: MailThread[]; nextCursor: string | undefined; seq: number };

/** Where the device keeps a workspace's strip (KeyValueStorage). */
export const mailTabsKey = (wsId: string) => `bundle.mailTabs.v1.${wsId}`;

/** The strip the device kept (empty when there's none or it can't be read). */
export function cachedMailTabs(raw: string | undefined): MailTabDef[] {
  if (raw === undefined) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? (v as MailTabDef[]) : [];
  } catch {
    return [];
  }
}

/** The file a workspace's inbox snapshot lives in, per user (SnapshotStore). */
export const mailSnapshotKey = (wsId: string, userId: string) => `mail-inbox.${wsId}.${userId}`;

/** What the device keeps of the inbox: enough for the first frame. `at`: when the server answered. */
export type MailSnapshot = {
  v: number; at: number; mailboxes: Mailbox[]; labels: MailLabel[]; tab: MailTabId; tabs: MailTabSummary[]; priority?: MailPrioritySummary; threads: MailThread[]; nextCursor?: string;
};

/** The snapshot in `raw`, or undefined when it's unreadable or an older format. */
export function parseMailSnapshot(raw: string): MailSnapshot | undefined {
  try {
    const v = JSON.parse(raw) as Partial<MailSnapshot> | null;
    return v?.v === SNAPSHOT_VERSION && typeof v.at === "number" && typeof v.tab === "string" && [v.mailboxes, v.labels, v.tabs, v.threads].every(Array.isArray) ? (v as MailSnapshot) : undefined;
  } catch {
    return undefined;
  }
}

/** The strip's caption: "Updated just now", or how long ago ("Updated 2h ago"). */
export function mailUpdatedText(at: number, now: number): MessageDescriptor {
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return msg("mail.updated.justNow");
  if (minutes < 60) return msg("mail.updated.minutes", { count: String(minutes) });
  if (minutes < 1440) return msg("mail.updated.hours", { count: String(Math.floor(minutes / 60)) });
  return msg("mail.updated.days", { count: String(Math.floor(minutes / 1440)) });
}

/** Mail's badge: unread Priority threads (a server without Priority: unread inbox threads). */
const badge = (mailboxes: Mailbox[]) => mailboxes.reduce((n, m) => n + (m.priorityUnread ?? m.unread), 0);

/** "Ana, Bia (3)" for a thread row. */
export function threadSenders(t: MailThread): string {
  const names = t.participants.slice(0, 3).map(displayName);
  const s = names.length === 0 ? "" : names.join(", ");
  return t.messageCount > 1 ? `${s} (${t.messageCount})` : s;
}

function toRow(t: MailThread): MailThreadRow {
  return {
    id: t.id, thread: t, senders: threadSenders(t), subject: t.subject, snippet: t.snippet, unread: t.unread, starred: t.starred,
    hasAttachments: t.hasAttachments, phishing: t.phishing === true, needsReply: t.needsReply === true, lastAt: t.lastAt,
    ...(t.priority === undefined ? {} : { priority: t.priority }),
  };
}

export class MailboxViewModel extends ViewModel<MailboxViewState> {
  private generation = 0;
  /** Each list seen or prefetched, by `listKey` (insertion order = age). */
  private readonly lists = new Map<string, CachedList>();
  /** Mailboxes whose inbox tabs were prefetched. */
  private readonly prefetched = new Set<string>();
  /**
   * Requests are numbered as they start (and local changes as they happen): only a later one's counts replace the shown
   * ones, and only a later one's list replaces a kept one.
   */
  private countsSeq = 0;
  private countsShown = 0;
  /** The tab was Mail's pick (the route has none): an empty Priority turns it into Email once the list says so. */
  private landing = true;
  /** The mailbox the Mailbox filter picked. */
  private pickedId: string | undefined;
  /** The tab the last `show` asked for (null before the first). */
  private routeTab: MailTabId | undefined | null = null;
  private cancelReload: (() => void) | undefined;
  /** Listings in flight, by mailbox and query. */
  private readonly inFlight = new Map<string, Promise<ListMailThreadsResponse>>();
  /** The search in flight, so a newer `load` can cancel it. */
  private searching: { key: string; abort: AbortController } | undefined;
  /** `SnapshotStore`'s key for this workspace and user. */
  private readonly snapshotKey: string;
  /** Mail opened from a snapshot: its tab holds, whatever the first answer says. */
  private restored = false;
  private cancelSave: (() => void) | undefined;
  /** The `revalidate` running, if any. */
  private revalidating: Promise<void> | undefined;
  /** The list it's over and that list's newest row, until an answer settles it. `live`: a reload for mail that came in, which only marks new rows. */
  private watching: { key: string; newest: number; live?: boolean } | undefined;
  private cancelUpdating: (() => void) | undefined;
  private cancelCaption: (() => void) | undefined;
  private cancelNewRows: (() => void) | undefined;
  private atTop = true;

  constructor(private readonly ws: WorkspaceViewModel) {
    const tabDefs = cachedMailTabs(ws.deps.storage.get(mailTabsKey(ws.wsId)));
    super({
      status: "loading", mailboxes: [], unified: false, filters: {}, folder: "inbox", tab: mailLandingTab(tabDefs, undefined) ?? "", tabDefs, labels: [], drafts: [], selected: [], query: "",
      threads: [], rows: [], tabs: [], loadingMore: false, unread: 0, offline: !ws.state.network, freshness: "idle", newIds: [],
    });
    this.snapshotKey = mailSnapshotKey(ws.wsId, ws.state.meId);
    this.onDispose(ws.subscribe(() => this.update({ offline: !ws.state.network })));
    this.onDispose(() => this.cancelReload?.());
    this.onDispose(() => {
      this.cancelSave?.();
      this.cancelUpdating?.();
      this.cancelCaption?.();
      this.cancelNewRows?.();
    });
    this.restore();
  }

  private get api() {
    return this.ws.deps.api;
  }

  /** Loads my mailboxes, then the current folder. */
  async refresh(fresh = false): Promise<void> {
    try {
      const mailboxes = await this.api.listMailboxes(this.ws.wsId);
      if (this.isDisposed) return;
      this.setMailboxes(mailboxes);
      if (this.state.mailboxId === undefined) {
        this.update({ status: "empty", threads: [], rows: [] });
        this.ws.deps.snapshots?.remove(this.snapshotKey);
        return;
      }
      await Promise.all([this.load(fresh), this.loadLabels()]);
    } catch {
      if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
    }
  }

  /** Reloads just my mailboxes (their From lists and reply choice changed in You › Mail); the list stays. */
  async reloadMailboxes(): Promise<void> {
    try {
      const mailboxes = await this.api.listMailboxes(this.ws.wsId);
      if (!this.isDisposed) this.setMailboxes(mailboxes);
    } catch {
      // Offline: the next refresh brings them.
    }
  }

  /**
   * Refreshes what's on screen where the user can see it: on open, back in the foreground and on pull (`pulled`: the
   * gesture has its own spinner, so no line). One at a time. A search, a filtered list or my drafts just refresh.
   */
  revalidate(pulled = false): Promise<void> {
    if (this.revalidating !== undefined) return this.revalidating;
    const run = this.watchRefresh(pulled).finally(() => {
      this.revalidating = undefined;
    });
    this.revalidating = run;
    return run;
  }

  /** Mail came on screen, or the app came back: a `revalidate`, unless the server has only just answered. */
  freshen(): void {
    if (this.state.status !== "ready") return;
    const at = this.state.lastUpdatedAt;
    if (at === undefined || this.ws.deps.clock.now() - at >= FRESHEN_GAP_MS) void this.revalidate();
  }

  /** The screen says whether the top of the list is in view: new rows' accent runs then; until then the "N new" pill stands in. */
  setAtTop(atTop: boolean): void {
    this.atTop = atTop;
    this.cancelNewRows?.();
    this.cancelNewRows = undefined;
    if (atTop && this.state.newIds.length > 0) this.clearNewRowsSoon();
  }

  private async watchRefresh(pulled: boolean): Promise<void> {
    const key = this.listKey();
    if (key === undefined || this.state.status !== "ready") return this.refresh(true);
    this.endFreshness();
    this.watching = { key, newest: this.newestRow() };
    if (!pulled) this.cancelUpdating = this.ws.deps.clock.setTimer(UPDATING_MS, () => this.update({ freshness: "updating" }));
    await this.refresh(true);
    if (this.isDisposed || this.watching === undefined || this.watching.live === true) return;
    // No answer came: the list stays, and says how old it is.
    this.watching = undefined;
    this.cancelUpdating?.();
    this.update({ freshness: this.state.lastUpdatedAt !== undefined && key === this.listKey() ? "stale" : "idle" });
  }

  private newestRow(): number {
    return this.state.threads.reduce((n, t) => Math.max(n, t.lastAt), 0);
  }

  /** The server answered for the list in view (now in `threads`): the strip and the new rows follow. */
  private settle(threads: MailThread[]): void {
    const watching = this.watching;
    this.watching = undefined;
    this.cancelUpdating?.();
    if (watching === undefined || watching.key !== this.listKey()) {
      if (this.state.freshness !== "idle") this.update({ freshness: "idle" });
      return;
    }
    const added = threads.filter((t) => t.lastAt > watching.newest && (watching.live !== true || t.unread)).map((t) => t.id);
    if (added.length === 0) {
      if (watching.live === true) return;
      this.update({ freshness: "updated" });
      this.cancelCaption = this.ws.deps.clock.setTimer(UPDATED_MS, () => {
        this.cancelCaption = undefined;
        this.update({ freshness: "idle" });
      });
      return;
    }
    this.update({ freshness: "idle" });
    this.markNew(added);
  }

  private markNew(added: string[]): void {
    this.update({ newIds: [...added, ...this.state.newIds.filter((id) => !added.includes(id))] });
    if (this.atTop) this.clearNewRowsSoon();
  }

  private clearNewRowsSoon(): void {
    this.cancelNewRows?.();
    this.cancelNewRows = this.ws.deps.clock.setTimer(NEW_ROW_MS, () => {
      this.cancelNewRows = undefined;
      this.update({ newIds: [] });
    });
  }

  /** Another list is open, or a new refresh starts: nothing the strip said still stands. */
  private endFreshness(): void {
    this.watching = undefined;
    this.cancelUpdating?.();
    this.cancelCaption?.();
    this.cancelCaption = undefined;
    if (this.state.freshness !== "idle") this.update({ freshness: "idle" });
  }

  /** My mailboxes changed or arrived: what the list reads follows (a mailbox I've lost leaves the filter). */
  private setMailboxes(mailboxes: Mailbox[]): void {
    const { mailbox, ...others } = this.state.filters;
    const picked = mailboxes.find((m) => m.address === mailbox || m.id === this.pickedId);
    this.pickedId = picked?.id;
    const filters: MailFilters = picked === undefined ? others : { ...others, mailbox: picked.address };
    const mailboxId = homeMailboxId(mailboxes, picked?.id);
    const { mailboxId: _old, ...rest } = this.state;
    void _old;
    this.setState({ ...rest, mailboxes, unread: badge(mailboxes), filters, unified: mailboxes.length > 1 && picked === undefined, ...(mailboxId === undefined ? {} : { mailboxId }) });
  }

  retry(): void {
    this.update({ status: "loading" });
    void this.refresh();
  }

  /**
   * Shows a route's mailbox/folder/tab (the screen calls it when the URL changes). No mailbox = all of mine; `group` keeps
   * what came through a group (`to:` it). No tab = Mail's pick: the strip's first, Priority, unless it's known to be
   * empty; it stays put while the route keeps having none. Unread, Starred and the rest of the filter row go with the
   * list they were set on; the mailbox stays. A route that carries the search (`query`) and the filter row (`filters`,
   * logic/mailQuery `mailListFilters`) sets them: a screen that remounts on the same route (a layout change, Back from a
   * thread) finds its search and chips as they were, and nothing reloads.
   */
  show(opts: { mailboxId?: string | undefined; group?: string | undefined; folder?: MailView | undefined; tab?: MailTabId | undefined; label?: string | undefined; query?: string | undefined; filters?: MailFilters | undefined }): void {
    // Complete file protection can refuse the read of a launch behind the lock screen: the user's here now.
    if (this.routeTab === null && this.state.status === "loading") this.restore();
    const switching = opts.mailboxId !== this.pickedId || opts.group !== this.state.filters.group;
    const keep = opts.tab === undefined && (this.routeTab === null ? this.restored : this.routeTab === undefined) && !switching;
    const tab = opts.tab ?? (keep ? this.state.tab : (mailLandingTab(this.state.tabDefs, switching ? undefined : this.state.priority) ?? ""));
    if (!keep) this.landing = opts.tab === undefined;
    this.routeTab = opts.tab;
    const folderNow = opts.folder ?? "inbox";
    const moved = folderNow !== this.state.folder || tab !== this.state.tab || opts.label !== this.state.label;
    const query = opts.query ?? "";
    const listedNow = listFiltersOf(this.state.filters);
    const listed = opts.filters === undefined ? (moved ? {} : listedNow) : listFiltersOf(opts.filters);
    const same = !moved && !switching && this.state.query === query && mailFilterQuery(listed) === mailFilterQuery(listedNow);
    const { label: _old, priority: known, ...rest } = this.state;
    void _old;
    this.pickedId = opts.mailboxId;
    const box = this.state.mailboxes.find((m) => m.id === opts.mailboxId);
    const filters: MailFilters = {
      ...listed,
      ...(box === undefined ? {} : { mailbox: box.address }),
      ...(opts.group === undefined ? {} : { group: opts.group }),
    };
    // Another mailbox's Priority isn't known yet.
    this.setState({ ...rest, folder: folderNow, tab, query, filters, ...(switching || known === undefined ? {} : { priority: known }), ...(opts.label === undefined ? {} : { label: opts.label }) });
    if (this.state.mailboxes.length > 0) this.setMailboxes(this.state.mailboxes);
    if (!same) this.enter();
    if (!same || this.state.status === "loading") void (this.state.mailboxes.length === 0 ? this.refresh() : this.load());
    if (switching) void this.loadLabels();
  }

  /** The Mailbox filter: one mailbox, or none for all of mine. */
  selectMailbox(mailboxId: string | undefined): void {
    this.show({ mailboxId, folder: this.state.folder, tab: this.state.tab, ...(this.state.label === undefined ? {} : { label: this.state.label }) });
  }

  /** The Mailbox filter on a group: what came through it, across my mailboxes. */
  selectGroup(group: string | undefined): void {
    this.show({ group, folder: this.state.folder, tab: this.state.tab, ...(this.state.label === undefined ? {} : { label: this.state.label }) });
  }

  setTab(tab: MailTabId): void {
    this.show({ ...this.route(), folder: "inbox", tab });
  }

  setFolder(folder: MailView): void {
    this.show({ ...this.route(), folder, tab: this.state.tab });
  }

  /** What stays in the route as the list changes. */
  private route(): { mailboxId?: string; group?: string } {
    const { group } = this.state.filters;
    return { ...(this.pickedId === undefined ? {} : { mailboxId: this.pickedId }), ...(group === undefined ? {} : { group }) };
  }

  /** Unread, Starred, Attachment, From, Date, Label: on the list in view. `undefined` turns one off. */
  setFilters(patch: { [K in keyof MailFilters]?: MailFilters[K] | undefined }): void {
    const filters = { ...this.state.filters } as Record<string, unknown>;
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete filters[k];
      else filters[k] = v;
    }
    this.update({ filters: filters as MailFilters });
    this.enter();
    void this.load();
  }

  /** The Date filter: the last `days` days (0 = today), `undefined` for any time. */
  setDateFilter(days: number | undefined): void {
    this.setFilters({ after: days === undefined ? undefined : this.dayBefore(days) });
  }

  /** The date `days` before today, as a filter writes it. */
  dayBefore(days: number): string {
    const now = this.ws.deps.clock.now();
    return new Date(now + this.ws.deps.clock.tzOffsetMinutes(now) * 60_000 - days * 86_400_000).toISOString().slice(0, 10);
  }

  /** Clear: every filter, the mailbox too. */
  clearFilters(): void {
    this.show({ folder: this.state.folder, tab: this.state.tab, ...(this.state.label === undefined ? {} : { label: this.state.label }) });
    if (hasListFilters(this.state.filters)) this.setFilters({ unread: undefined, starred: undefined, attachment: undefined, from: undefined, after: undefined, before: undefined, label: undefined });
  }

  /** Search across what's in view ("" goes back to the folder). */
  search(query: string): void {
    // Typing on in a search keeps the results shown until the next ones arrive; starting or leaving one is a new list.
    const typing = query.trim() !== "" && this.state.query.trim() !== "";
    this.update({ query });
    // What's on the device shows at once; the server's answer, in the same order, replaces it.
    const local = query.trim() === "" ? [] : this.localMatches();
    if (local.length > 0) {
      this.update({ status: "ready" });
      this.setThreads(local, undefined);
    } else if (!typing) this.enter();
    void this.load();
  }

  /** The newest conversations in the kept lists that match the search. */
  private localMatches(): MailThread[] {
    const { query, labels, folder, mailboxes } = this.state;
    // The kept lists know nothing of the filter row, so its results wait for the server.
    if (this.filterQuery() !== "") return [];
    const matches = mailQueryLocal(parseMailQuery(query.trim()), labels, folder, mailboxes);
    if (matches === undefined) return [];
    const found = new Map<string, MailThread>();
    for (const list of this.lists.values()) {
      if (list.view.mailboxId !== this.target) continue;
      for (const t of list.threads) if (matches(t)) found.set(t.id, t);
    }
    return [...found.values()].sort((a, b) => b.lastAt - a.lastAt || (a.id < b.id ? 1 : -1)).slice(0, PAGE);
  }

  async loadMore(): Promise<void> {
    const cursor = this.state.nextCursor;
    const target = this.target;
    if (cursor === undefined || target === undefined || this.state.loadingMore) return;
    this.update({ loadingMore: true });
    const gen = this.generation;
    const key = this.listKey();
    try {
      const r = await this.fetch(target, { ...this.query(cursor), counts: "none" });
      if (gen !== this.generation || key !== this.listKey() || this.isDisposed) return;
      const threads = [...this.state.threads, ...r.threads.filter((t) => !this.state.threads.some((x) => x.id === t.id))];
      this.setTabDefs(r.tabDefs);
      this.setThreads(threads, r.nextCursor);
    } finally {
      if (!this.isDisposed) this.update({ loadingMore: false });
    }
  }

  // ---------- Thread actions (optimistic) ----------

  setUnread(threadId: string, unread: boolean): void {
    this.patch(threadId, { unread });
  }

  setStarred(threadId: string, starred: boolean): void {
    this.patch(threadId, { starred });
  }

  /** Each move below → its undo (Gmail's "Undo" on the toast): every conversation back to the folder it was in. */
  archive(threadId: string): () => void {
    return this.moveTo([threadId], "archive");
  }

  trash(threadId: string): () => void {
    return this.moveTo([threadId], "trash");
  }

  markSpam(threadId: string, spam: boolean): () => void {
    return this.moveTo([threadId], spam ? "spam" : "inbox");
  }

  /** Back to the inbox (from Archive, Trash or Spam). */
  moveToInbox(threadId: string): () => void {
    return this.moveTo([threadId], "inbox");
  }

  // ---------- Bulk (Gmail's checkboxes) ----------

  toggleSelected(threadId: string): void {
    const on = this.state.selected.includes(threadId);
    this.update({ selected: on ? this.state.selected.filter((x) => x !== threadId) : this.state.rows.map((r) => r.id).filter((id) => id === threadId || this.state.selected.includes(id)) });
  }

  /** Every conversation in the list, or none when they all are already. */
  toggleAllSelected(): void {
    const all = this.state.rows.map((r) => r.id);
    this.update({ selected: all.length > 0 && all.every((id) => this.state.selected.includes(id)) ? [] : all });
  }

  clearSelection(): void {
    if (this.state.selected.length > 0) this.update({ selected: [] });
  }

  /** Moves the ticked conversations (archive / trash / spam / inbox) → the undo. Clears the selection. */
  moveSelected(folder: MailFolder): () => void {
    const ids = this.state.selected;
    this.update({ selected: [] });
    return this.moveTo(ids, folder);
  }

  /** Read / unread for the ticked conversations. */
  setSelectedUnread(unread: boolean): void {
    for (const id of this.state.selected) this.patch(id, { unread });
    this.update({ selected: [] });
  }

  /** Adds (or, when every ticked one has it, removes) a label on the ticked conversations. */
  toggleSelectedLabel(labelId: string): void {
    const ts = this.state.threads.filter((t) => this.state.selected.includes(t.id));
    // Across mailboxes the label is its name: each conversation takes its own mailbox's, where there is one.
    const ids = mailLabelIds(this.state.labels, labelId);
    const own = (t: MailThread) => this.state.labels.find((l) => l.mailboxId === t.mailboxId && ids.includes(l.id))?.id;
    const all = ts.every((t) => (t.labelIds ?? []).some((id) => ids.includes(id)));
    for (const t of ts) {
      const now = t.labelIds ?? [];
      const id = own(t);
      if (id === undefined) continue;
      this.patch(t.id, { labelIds: all ? now.filter((x) => x !== id) : [...new Set([...now, id])] });
    }
    this.update({ selected: [] });
  }

  private moveTo(ids: string[], folder: MailFolder): () => void {
    const before = ids.map((id) => [id, this.state.threads.find((t) => t.id === id)?.folder] as const);
    for (const id of ids) this.patch(id, { folder });
    return () => {
      for (const [id, was] of before) if (was !== undefined && was !== folder) this.patch(id, { folder: was });
    };
  }

  /** Gmail's snooze: out of the inbox until `until` (null wakes it now). */
  snooze(threadId: string, until: number | null): void {
    this.patch(threadId, { snoozeUntil: until });
  }

  /** The labels on a conversation, replaced whole ("Label as"). */
  setLabels(threadId: string, labelIds: string[]): void {
    this.patch(threadId, { labelIds });
  }

  async createLabel(req: SaveMailLabelRequest): Promise<MailLabel | undefined> {
    const mailboxId = this.state.mailboxId;
    if (mailboxId === undefined) return undefined;
    try {
      const label = await this.api.createMailLabel(this.ws.wsId, mailboxId, req);
      if (!this.isDisposed) this.update({ labels: [...this.state.labels, label].sort((a, b) => a.name.localeCompare(b.name)) });
      return label;
    } catch {
      return undefined;
    }
  }

  deleteLabel(labelId: string): void {
    this.update({ labels: this.state.labels.filter((l) => l.id !== labelId) });
    void this.api.deleteMailLabel(this.ws.wsId, labelId).catch(() => void this.loadLabels());
  }

  /** Undo send / cancel a scheduled send: → the message as my draft again (the screen reopens the composer on it). */
  async undoSend(messageId: string): Promise<MailDraft | undefined> {
    try {
      const draft = await this.api.cancelMailSend(this.ws.wsId, messageId);
      if (!this.isDisposed && this.state.folder === "drafts") this.update({ drafts: [draft, ...this.state.drafts] });
      return draft;
    } catch {
      return undefined;
    }
  }

  discardDraft(draftId: string): void {
    this.update({ drafts: this.state.drafts.filter((d) => d.id !== draftId) });
    void this.api.deleteMailDraft(this.ws.wsId, draftId).catch(() => void this.load(true));
  }

  /** "Move to Clients": the sender's later mail goes there too. */
  moveToTab(threadId: string, tab: MailTabId): void {
    this.patch(threadId, { tab });
  }

  /** Mark as priority (true) or Not priority (false), Gmail's + / −: the conversation and its sender's later mail → the undo. */
  markPriority(threadId: string, on: boolean): () => void {
    return this.markPriorityOf([threadId], on);
  }

  /** Mark as priority / Not priority for the ticked conversations → the undo. Clears the selection. */
  markSelectedPriority(on: boolean): () => void {
    const ids = this.state.selected;
    this.update({ selected: [] });
    return this.markPriorityOf(ids, on);
  }

  private markPriorityOf(ids: string[], on: boolean): () => void {
    const before = ids.map((id) => [id, this.state.threads.find((t) => t.id === id)] as const);
    // The sender's other conversations follow the mark: refetch the list once the server has it.
    for (const id of ids) this.patch(id, { priority: on }, true);
    return () => {
      for (const [id, was] of before) {
        // Back in the list it left (Not priority on the Priority tab), then the mark it had.
        if (was !== undefined && !this.state.threads.some((t) => t.id === id)) this.applyThread(was);
        this.patch(id, { priority: was?.priorityMark ?? null }, true);
      }
    };
  }

  /** Realtime: a thread changed or arrived; a mailbox changed or went away. */
  onEvent(ev: PersistentEvent): void {
    if (ev.type === "mail.thread.updated") {
      const t = ev.thread;
      if (!this.state.unified) {
        if (t.mailboxId === this.state.mailboxId) this.applyLive(t);
      } else if (this.state.mailboxes.some((m) => m.id === t.mailboxId)) {
        // A thread the list hasn't got may be a copy of a row it has: the server decides.
        if (this.state.threads.some((x) => x.id === t.id)) this.applyLive(t);
        else if (this.visible(t)) this.reloadSoon();
        else void this.reloadTabs();
      }
      this.bumpUnread(t);
    } else if (ev.type === "mail.mailbox.updated" || ev.type === "mail.mailbox.deleted") {
      void this.refresh(true);
    }
  }

  private reloadSoon(): void {
    if (this.cancelReload !== undefined) return;
    this.cancelReload = this.ws.deps.clock.setTimer(RELOAD_MS, () => {
      this.cancelReload = undefined;
      if (this.isDisposed) return;
      const key = this.listKey();
      if (key !== undefined && this.state.status === "ready") this.watching ??= { key, newest: this.newestRow(), live: true };
      void this.load(true);
    });
  }

  /** A thread the server pushed: unread mail newer than the list had is a new row, like a refresh's (my own reply isn't). */
  private applyLive(t: MailThread): void {
    const arrived = this.state.status === "ready" && t.unread && t.lastAt > this.newestRow() && this.visible(t);
    this.applyThread(t);
    if (arrived) this.markNew([t.id]);
  }

  /** Whether the open folder/tab/search shows this thread. */
  private visible(t: MailThread): boolean {
    if (this.state.query !== "" || this.filterQuery() !== "") return this.state.threads.some((x) => x.id === t.id);
    const target = this.target;
    return target !== undefined && this.visibleIn(t, { mailboxId: target, folder: this.state.folder, label: this.state.label, tab: this.state.tab });
  }

  /** Whether a list shows this thread. */
  private visibleIn(t: MailThread, view: ListView): boolean {
    if (view.mailboxId !== ALL && t.mailboxId !== view.mailboxId) return false;
    const away = t.folder === "trash" || t.folder === "spam";
    const snoozed = (t.snoozedUntil ?? 0) > this.ws.deps.clock.now();
    if (view.label !== undefined) {
      const ids = mailLabelIds(this.state.labels, view.label);
      return !away && (t.labelIds ?? []).some((id) => ids.includes(id));
    }
    switch (view.folder) {
      case "drafts":
        return false;
      case "starred":
        return !away && t.starred;
      case "snoozed":
        return snoozed;
      case "scheduled":
        return t.scheduledAt !== undefined;
      case "sent":
        return !away && t.lastSentAt !== undefined;
      case "inbox":
        if (t.folder !== "inbox" || snoozed) return false;
        if (view.tab === "") return true;
        const def = this.state.tabDefs.find((d) => d.id === view.tab);
        if (def?.listsAll === true) return true;
        return def?.kind === "priority" ? t.priority !== undefined : t.tab === view.tab;
      default:
        return t.folder === view.folder;
    }
  }

  private applyThread(fresh: MailThread): void {
    // Only the unified list knows a row's copies and group: an update of the thread keeps them.
    const t = keepRowExtras(fresh, this.state.threads);
    // The kept lists follow it too (into the tab it moved to, out of the one it left).
    const current = this.listKey();
    for (const [key, list] of Array.from(this.lists.entries())) {
      if (key === current || (list.view.mailboxId !== ALL && list.view.mailboxId !== t.mailboxId)) continue;
      const had = list.threads.some((x) => x.id === t.id);
      const shows = this.visibleIn(t, list.view);
      // A kept unified list adds a row only through its own reload: the thread may be a copy of one it has.
      if (had || (shows && list.view.mailboxId !== ALL)) this.keep(key, { ...list, threads: place(list.threads, keepRowExtras(t, list.threads), shows), seq: ++this.countsSeq });
    }
    this.setThreads(place(this.state.threads, t, this.visible(t)), this.state.nextCursor);
    // Tab counts move with the thread: refetch them quietly.
    void this.reloadTabs();
  }

  private bumpUnread(t: MailThread): void {
    const mailboxes = this.state.mailboxes;
    if (!mailboxes.some((m) => m.id === t.mailboxId)) return;
    void this.api.listMailboxes(this.ws.wsId).then((list) => {
      if (!this.isDisposed) this.update({ mailboxes: list, unread: badge(list) });
    }, () => undefined);
  }

  private async reloadTabs(): Promise<void> {
    const target = this.target;
    if (target === undefined) return;
    const seq = ++this.countsSeq;
    try {
      const r = await this.fetch(target, { folder: "inbox", ...(this.state.tab === "" ? {} : { tab: this.state.tab }), limit: 1 });
      if (this.isDisposed || target !== this.target) return;
      this.setTabDefs(r.tabDefs);
      this.setCounts(seq, r.tabs, r.priority);
    } catch {
      // Keep the strip as it is.
    }
  }

  /** Optimistic update; `reload` refetches the list once the server has it (a change that moves other conversations). */
  private patch(threadId: string, req: UpdateMailThreadRequest, reload = false): void {
    const t = this.state.threads.find((x) => x.id === threadId);
    if (t !== undefined) {
      const { snoozeUntil, labelIds, priority, ...rest } = req;
      const next: MailThread = { ...t, ...rest };
      if (snoozeUntil === null) delete next.snoozedUntil;
      else if (snoozeUntil !== undefined) next.snoozedUntil = snoozeUntil;
      if (labelIds !== undefined) next.labelIds = labelIds;
      if (priority === null) {
        delete next.priorityMark;
        delete next.senderPriority;
      } else if (priority !== undefined) {
        next.priorityMark = priority;
        next.senderPriority = priority;
      }
      // Starring again after Not priority brings it back (the latest choice wins).
      if (req.starred === true && next.priorityMark === false) delete next.priorityMark;
      const reason = threadPriority(next, this.ws.deps.clock.now(), mailWaitTabs(this.state.tabDefs));
      if (reason === undefined) delete next.priority;
      else next.priority = reason;
      this.applyThread(next);
    }
    this.api.updateMailThread(this.ws.wsId, threadId, req).then(
      (thread) => {
        this.applyThread(thread);
        if (reload && !this.isDisposed) void this.load(true);
      },
      () => void this.load(true),
    );
  }

  /** What the list reads: one mailbox, or `ALL`. */
  private get target(): string | undefined {
    return this.state.unified ? ALL : this.state.mailboxId;
  }

  /** The filter row's text, without the mailbox a one-mailbox list already is. */
  private filterQuery(): string {
    const { mailbox: _mailbox, ...rest } = this.state.filters;
    void _mailbox;
    return mailFilterQuery(rest);
  }

  private fetch(target: string, q: Partial<ListMailThreadsQuery>, signal?: AbortSignal): Promise<ListMailThreadsResponse> {
    return target === ALL ? this.api.listAllMailThreads(this.ws.wsId, q, signal) : this.api.listMailThreads(this.ws.wsId, target, q, signal);
  }

  private query(cursor?: string) {
    const folder: MailListFolder = this.state.folder === "drafts" ? "inbox" : this.state.folder;
    const q: { folder: MailListFolder; tab?: MailTabId; label?: string; q?: string; filter?: string; cursor?: string; limit: number; counts?: "none" } = { folder, limit: PAGE };
    const filter = this.filterQuery();
    if (filter !== "") q.filter = filter;
    if (this.state.query.trim() !== "") {
      q.q = this.state.query.trim();
      q.counts = "none";
    }
    else if (this.state.label !== undefined) q.label = this.state.label;
    else if (folder === "inbox" && this.state.tab !== "") q.tab = this.state.tab;
    if (cursor !== undefined) q.cursor = cursor;
    return q;
  }

  /** One request for a list, however many ask while it's in flight; `fresh` wants what the server has now. */
  private listOnce(target: string, q: Partial<ListMailThreadsQuery>, fresh: boolean): Promise<ListMailThreadsResponse> {
    const key = JSON.stringify([target, q]);
    if (this.searching !== undefined && this.searching.key !== key) {
      this.searching.abort.abort();
      this.searching = undefined;
    }
    const open = this.inFlight.get(key);
    if (open !== undefined && !fresh) return open;
    const abort = q.q === undefined ? undefined : new AbortController();
    if (abort !== undefined) this.searching = { key, abort };
    const request = this.fetch(target, q, abort?.signal).finally(() => {
      if (this.inFlight.get(key) === request) this.inFlight.delete(key);
      if (this.searching?.abort === abort) this.searching = undefined;
    });
    this.inFlight.set(key, request);
    return request;
  }

  private async load(fresh = false): Promise<void> {
    const target = this.target;
    if (target === undefined) return;
    const gen = ++this.generation;
    if (this.state.folder === "drafts" && this.state.query.trim() === "" && this.state.label === undefined) {
      try {
        const lists = await Promise.all(this.boxes(target).map((id) => this.api.listMailDrafts(this.ws.wsId, id)));
        const drafts = lists.flat().toSorted((a, b) => b.updatedAt - a.updatedAt);
        if (gen !== this.generation || this.isDisposed) return;
        this.update({ status: "ready", drafts });
        this.setThreads([], undefined);
      } catch {
        if (gen === this.generation && !this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
      }
      return;
    }
    // The list this answer is for: if I've left it by the time it arrives, it isn't shown.
    const key = this.listKey();
    const view: ListView = { mailboxId: target, folder: this.state.folder, label: this.state.label, tab: this.state.tab };
    const seq = ++this.countsSeq;
    try {
      const query = this.query();
      const r = await this.listOnce(target, query, fresh);
      if (this.isDisposed) return;
      if (gen !== this.generation || key !== this.listKey() || target !== this.target) {
        // A list I've left: not shown, but kept for when I'm back (unless something newer is).
        if (key !== undefined && (this.lists.get(key)?.seq ?? -1) < seq) {
          this.keep(key, { view, threads: r.threads, nextCursor: r.nextCursor, seq });
        }
        return;
      }
      this.setTabDefs(r.tabDefs);
      if (query.counts === undefined) this.setCounts(seq, r.tabs, r.priority);
      const inbox = folder(query) === "inbox";
      // Mail's pick, now that the strip and Priority's count are known: another tab (Priority empty, or the strip wasn't
      // cached yet) loads that one instead, still loading, so the wrong list never flashes.
      const pick = this.landing && inbox ? mailLandingTab(r.tabDefs, r.priority ?? { total: 0 }) : undefined;
      if (inbox) this.landing = false;
      if (pick !== undefined && pick !== query.tab) {
        this.update({ tab: pick });
        this.enter();
        return this.load(fresh);
      }
      this.update({ status: "ready", lastUpdatedAt: this.ws.deps.clock.now() });
      this.setThreads(r.threads, r.nextCursor);
      this.settle(r.threads);
      if (inbox && query.filter === undefined) void this.prefetchTabs(target);
    } catch {
      if (gen !== this.generation || this.isDisposed) return;
      if (this.watching?.live === true) this.watching = undefined;
      if (this.state.status === "loading") this.update({ status: "failed" });
      else if (key !== undefined && this.watching === undefined && this.state.lastUpdatedAt !== undefined) this.update({ freshness: "stale" });
    }
  }

  /** The mailboxes a target reads. */
  private boxes(target: string): string[] {
    return target === ALL ? this.state.mailboxes.map((m) => m.id) : [target];
  }

  private async loadLabels(): Promise<void> {
    const target = this.target;
    if (target === undefined) return;
    try {
      const lists = await Promise.all(this.boxes(target).map((id) => this.api.listMailLabels(this.ws.wsId, id)));
      if (!this.isDisposed && target === this.target) this.update({ labels: lists.flat().toSorted((a, b) => a.name.localeCompare(b.name)) });
    } catch {
      // The rail shows no labels until the next refresh.
    }
  }

  /** The server's strip: shown, and kept on the device for the next start. */
  private setTabDefs(tabDefs: MailTabDef[]): void {
    const raw = JSON.stringify(tabDefs);
    if (raw === JSON.stringify(this.state.tabDefs)) return;
    this.ws.deps.storage.set(mailTabsKey(this.ws.wsId), raw);
    this.update({ tabDefs });
  }

  /** The open list's key in `lists`; none for a search or my drafts (never kept). */
  private listKey(): string | undefined {
    const { folder, label, tab, query } = this.state;
    const target = this.target;
    if (target === undefined || query.trim() !== "" || this.filterQuery() !== "" || (folder === "drafts" && label === undefined)) return undefined;
    return listKeyOf({ mailboxId: target, folder, label, tab });
  }

  /**
   * A new list is open: its kept copy at once (refreshed by the `load` that follows), or a skeleton until it arrives —
   * never the list I left.
   */
  private enter(): void {
    this.endFreshness();
    this.cancelNewRows?.();
    if (this.state.newIds.length > 0) this.update({ newIds: [] });
    const key = this.listKey();
    const kept = key === undefined ? undefined : this.lists.get(key);
    if (kept !== undefined) {
      this.update({ status: "ready" });
      this.setThreads(kept.threads, kept.nextCursor);
    } else if (this.state.mailboxes.length > 0 || this.state.status !== "loading") {
      this.update({ status: "loading", loadingMore: false });
      this.setThreads([], undefined);
    }
  }

  /** The strip's counts and previews from a request numbered `seq`: shown unless a later request's already are. */
  private setCounts(seq: number, tabs: MailTabSummary[], priority: MailPrioritySummary | undefined): void {
    if (seq < this.countsShown) return;
    this.countsShown = seq;
    const same = JSON.stringify(tabs) === JSON.stringify(this.state.tabs) && (priority === undefined || JSON.stringify(priority) === JSON.stringify(this.state.priority));
    if (!same) {
      this.update({ tabs, ...(priority === undefined ? {} : { priority }) });
      this.saveSoon();
    }
  }

  /** Gmail's head start: the first page of the strip's other tabs, once per mailbox after its inbox has loaded. */
  private async prefetchTabs(target: string): Promise<void> {
    if (this.prefetched.has(target)) return;
    this.prefetched.add(target);
    await Promise.all(
      this.state.tabDefs.map(async (def) => {
        const view: ListView = { mailboxId: target, folder: "inbox", label: undefined, tab: def.id };
        const key = listKeyOf(view);
        if (this.lists.has(key)) return;
        const seq = ++this.countsSeq;
        try {
          const r = await this.fetch(target, { folder: "inbox", tab: def.id, limit: PAGE, counts: "none" });
          // A list opened (and loaded or changed) meanwhile is newer; the open one is the screen's.
          if (this.isDisposed || (this.lists.get(key)?.seq ?? -1) > seq || key === this.listKey()) return;
          this.keep(key, { view, threads: r.threads, nextCursor: r.nextCursor, seq });
        } catch {
          // Opening the tab loads it.
        }
      }),
    );
  }

  private keep(key: string, list: CachedList): void {
    this.lists.delete(key);
    this.lists.set(key, list);
    if (this.lists.size > CACHED_LISTS) {
      const oldest = this.lists.keys().next().value;
      if (oldest !== undefined) this.lists.delete(oldest);
    }
  }

  private setThreads(threads: MailThread[], nextCursor: string | undefined): void {
    const key = this.listKey();
    const { folder, label, tab } = this.state;
    const target = this.target;
    if (key !== undefined && target !== undefined && this.state.status === "ready") this.keep(key, { view: { mailboxId: target, folder, label, tab }, threads, nextCursor, seq: ++this.countsSeq });
    // Ticked conversations that left the list aren't ticked any more.
    const selected = this.state.selected.filter((id) => threads.some((t) => t.id === id));
    const newIds = this.state.newIds.filter((id) => threads.some((t) => t.id === id));
    const patch: Partial<MailboxViewState> = {
      threads, rows: threads.map(toRow), ...(selected.length === this.state.selected.length ? {} : { selected }), ...(newIds.length === this.state.newIds.length ? {} : { newIds }),
    };
    if (nextCursor === undefined) {
      const { nextCursor: _drop, ...rest } = this.state;
      void _drop;
      this.setState({ ...rest, ...patch });
    } else this.update({ ...patch, nextCursor });
    this.saveSoon();
  }

  /** The device's inbox, if it has one: on screen at once, as ready, on its tab. One it can't read is dropped. */
  private restore(): void {
    const store = this.ws.deps.snapshots;
    const raw = store?.read(this.snapshotKey);
    if (raw === undefined) return;
    const snapshot = parseMailSnapshot(raw);
    if (snapshot === undefined || snapshot.mailboxes.length === 0) {
      store?.remove(this.snapshotKey);
      return;
    }
    this.restored = true;
    this.landing = false;
    this.setMailboxes(snapshot.mailboxes);
    this.update({ status: "ready", tab: snapshot.tab, tabs: snapshot.tabs, labels: snapshot.labels, lastUpdatedAt: snapshot.at, ...(snapshot.priority === undefined ? {} : { priority: snapshot.priority }) });
    this.setThreads(snapshot.threads, snapshot.nextCursor);
    this.cancelSave?.();
  }

  private saveSoon(): void {
    if (this.ws.deps.snapshots === undefined || this.isDisposed) return;
    this.cancelSave?.();
    this.cancelSave = this.ws.deps.clock.setTimer(SNAPSHOT_SAVE_MS, () => {
      this.cancelSave = undefined;
      this.save();
    });
  }

  /** Keeps the inbox on the device: only all my mailboxes, unfiltered, which is what Mail opens on. */
  private save(): void {
    const { status, folder, label, mailboxes, labels, tab, tabs, priority, threads, nextCursor, lastUpdatedAt, filters } = this.state;
    if (status !== "ready" || lastUpdatedAt === undefined || folder !== "inbox" || label !== undefined || this.listKey() === undefined || this.pickedId !== undefined || filters.group !== undefined) return;
    const snapshot: MailSnapshot = {
      v: SNAPSHOT_VERSION, at: lastUpdatedAt, mailboxes, labels, tab, tabs, threads: threads.slice(0, PAGE),
      ...(priority === undefined ? {} : { priority }),
      // The cursor of a list I scrolled is past the first page.
      ...(nextCursor === undefined || threads.length > PAGE ? {} : { nextCursor }),
    };
    this.ws.deps.snapshots?.write(this.snapshotKey, () => JSON.stringify(snapshot));
  }
}

const listKeyOf = (v: ListView) => [v.mailboxId, v.folder, v.label ?? "", v.folder === "inbox" && v.label === undefined ? v.tab : ""].join("\u0000");

/** `t` with the row's copies and group, which only the unified list's answer carries. */
function keepRowExtras(t: MailThread, rows: MailThread[]): MailThread {
  const row = rows.find((x) => x.id === t.id);
  if (row === undefined || (row.copies === undefined && row.via === undefined)) return t;
  return { ...t, ...(row.copies === undefined ? {} : { copies: row.copies }), ...(row.via === undefined ? {} : { via: row.via }) };
}

/** A list with `t` placed (newest first) or taken out. */
function place(threads: MailThread[], t: MailThread, shows: boolean): MailThread[] {
  const others = threads.filter((x) => x.id !== t.id);
  return shows ? [t, ...others].sort((a, b) => b.lastAt - a.lastAt || (a.id < b.id ? 1 : -1)) : others;
}

/** The listing of the inbox's tabs (no search, label or other folder): Mail's pick applies to it. */
function folder(q: { folder: MailListFolder; q?: string; label?: string }): MailListFolder | undefined {
  return q.q === undefined && q.label === undefined ? q.folder : undefined;
}
